<template>
  <div class="w-full h-full relative flex flex-col">
    <!-- Disclaimer Banner -->
    <div
      v-if="disclaimerText"
      class="w-full z-50 text-center text-sm shadow-md"
      v-html="disclaimerText"
    ></div>

    <!-- Quick toggles. Renders only when the embed authored some, so every
         existing embed keeps looking exactly as it does today. -->
    <div
      v-if="toggleOptions.length"
      class="w-full z-50 flex flex-wrap gap-2 px-3 py-2 bg-white shadow-sm"
    >
      <button
        type="button"
        class="px-3 py-1 text-sm rounded-full border"
        :class="activeToggle ? 'border-gray-300 text-gray-600' : 'border-gray-800 bg-gray-800 text-white'"
        @click="selectToggle(null)"
      >
        All
      </button>
      <button
        v-for="option in toggleOptions"
        :key="option.type + ':' + option.value"
        type="button"
        class="px-3 py-1 text-sm rounded-full border"
        :class="isActiveToggle(option) ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-300 text-gray-600'"
        @click="selectToggle(option)"
      >
        {{ option.label }}
      </button>
    </div>

    <div class="relative flex-grow w-full h-full">
      <div
        v-if="loading"
        class="absolute inset-0 z-40 flex items-center justify-center bg-gray-100 opacity-75"
      >
        <div class="loader"></div>
      </div>

      <MapComponent
        ref="mapComp"
        :items="mapItems"
        :center="center"
        :labels="labelsDefault"
        @art-click="handleArtClick"
        class="w-full h-full"
      />
    </div>
  </div>
</template>

<script>
import artAroundApi from '../api'
import MapComponent from '../components/MapComponent'
import { filterArts } from '../features/arts/filter'
import { parseToggles } from '../features/arts/toggles'
import { parseLabelsAttribute } from '../features/map/labels'

export default {
  name: 'EmbedMap',
  components: {
    MapComponent,
  },
  props: {
    disclaimerText: {
      type: String,
      default: '',
    },
    city: { type: String, default: null },
    year: { type: String, default: null },
    artist: { type: String, default: null },
    commissioner: { type: String, default: null },
    // Comma-separated toggle entries, optionally `Label=Value` and optionally
    // `type:` prefixed to filter a field other than `tag`. Which toggles a
    // given embed offers is per-site configuration -- see parseToggles.
    toggles: { type: String, default: '' },
    // `off` hides the street-label overlay -- see parseLabelsAttribute for the
    // accepted spellings. A String, not a Boolean, because it arrives from an
    // HTML attribute exactly like `toggles` does; absent means on, so embeds
    // authored before this feature gain the labels instead of losing them.
    labels: { type: String, default: '' },
  },
  data() {
    return {
      allArts: [], // Store full dataset
      loading: true,
      center: { lat: 41.8781136, lng: -87.6297982 }, // Default Chicago
      queryParams: {}, // Store URL query params
      activeToggle: null, // { type, value } of the selected quick toggle, if any
    }
  },
  computed: {
    // Merge props and URL params to determine active filters
    activeFilters() {
      return {
        ...this.queryParams,
        ...(this.city && { city: this.city }),
        ...(this.year && { year: this.year }),
        ...(this.artist && { artist: this.artist }),
        ...(this.commissioner && { commissioner: this.commissioner }),
        // Last so a selected toggle wins over a same-named query param: the
        // visitor clicked it after the page loaded, so it is the more recent
        // intent. The key is whichever field the toggle was typed for (`tag`
        // by default), not always `tag`.
        ...(this.activeToggle && { [this.activeToggle.type]: this.activeToggle.value }),
      }
    },
    toggleOptions() {
      return parseToggles(this.toggles)
    },
    labelsDefault() {
      return parseLabelsAttribute(this.labels)
    },
    // Filter the full dataset based on active filters
    mapItems() {
      return filterArts(this.allArts, this.activeFilters)
    },
  },
  async mounted() {
    // Parse URL params once on mount
    const urlParams = new URLSearchParams(window.location.search)
    this.queryParams = Object.fromEntries(urlParams.entries())

    await this.fetchMapData()
  },
  methods: {
    async fetchMapData() {
      this.loading = true
      try {
        // Fetch ALL data (backend ignores params, global cache)
        const mapArts = await artAroundApi.getMapArts({})
        this.allArts = mapArts
      } catch (e) {
        console.error('Error loading map data', e)
      } finally {
        this.loading = false
      }
    },
    // `option` is a whole `{label, type, value}` toggle option, or `null` for
    // the "All" button, which always clears the active toggle regardless of
    // what it currently holds.
    selectToggle(option) {
      if (!option) {
        this.activeToggle = null
        return
      }
      // Clicking the active toggle clears it, so the bar needs no separate
      // "off" state beyond All.
      this.activeToggle = this.isActiveToggle(option)
        ? null
        : { type: option.type, value: option.value }
    },
    // Vue templates can't cleanly inline an object-equality check, so this
    // compares both `type` and `value` -- two toggles can share a `value`
    // (e.g. a tag and a category of the same name) and must not be conflated.
    isActiveToggle(option) {
      return (
        !!this.activeToggle &&
        this.activeToggle.type === option.type &&
        this.activeToggle.value === option.value
      )
    },
    handleArtClick(id) {
      const baseUrl = artAroundApi.getBaseUrl()
      window.open(`${baseUrl}/art/${id}`, '_blank')
    },
    fitBounds() {
      const map = this.$refs.mapComp
      if (!map) return
      // An embed with no filter shows the worldwide collection; framing all of
      // it is a z1 globe, so open on Americas + Europe instead (HOME_BOUNDS).
      const f = this.activeFilters
      // Enumerates the specific keys `activeFilters` can carry: the four
      // props plus whichever field the active toggle set (`tag` or, now,
      // `category` -- the only toggle-only field, since it has no prop).
      const filtered = !!(
        f.city ||
        f.year ||
        f.artist ||
        f.commissioner ||
        f.tag ||
        f.category
      )
      if (filtered && this.mapItems.length) map.fitBounds(this.mapItems)
      else map.fitHome()
    },
  },
  watch: {
    // A host page may rewrite the `toggles` attribute at any time -- the demo
    // page does exactly that. Without this the map would keep filtering on a
    // tag whose button is no longer on screen, with no way to clear it.
    toggleOptions(options) {
      if (this.activeToggle && !options.some(o => this.isActiveToggle(o))) {
        this.activeToggle = null
      }
    },
    loading(val) {
      if (!val && this.mapItems.length > 0) {
        this.$nextTick(() => {
          this.fitBounds()
        })
      }
    },
    // Watch for filter changes to re-fit bounds if needed
    mapItems(newItems) {
      if (!this.loading && newItems.length > 0) {
        // Optional: Auto-zoom when filters change?
        // this.fitBounds()
        // Keeping it consistent with previous behavior: might not auto-zoom on filter change unless desired.
      }
    },
  },
}
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
